import { FC } from 'react';

import { BoardSteps } from 'utils/constants';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import NpkInputSection from './components/NpkInputSection';
import ExpectedGainsSection from './components/ExpectedGainsSection';
import PricesSection from './components/PricesSection';
import TotalGainSection from './components/TotalGainSection';
import ResetButton from './components/ResetButton';
import { useFertilizationCalculations } from './hooks/useFertilizationCalculations';
import { useFertilizationInputs } from './hooks/useFertilizationInputs';
import { useFertilizationNavigation } from './hooks/useFertilizationNavigation';

interface Props {
  isPDF?: boolean;
}

const FertilizationBoard: FC<Props> = ({ isPDF }) => {
  const { step, kgHa, kg, onlyAc } = useCroptimizer();

  const isActive = step === BoardSteps.fertilization;

  // Custom hooks
  const {
    inputN,
    setInputN,
    cleaninputn,
    inputP,
    setInputP,
    cleaninputp,
    inputK,
    setInputK,
    cleaninputk,
    pricesN,
    setPricesN,
    cleanpricesn,
    pricesP,
    setPricesP,
    cleanpricesp,
    pricesK,
    setPricesK,
    cleanpricesk,
    efficiencyN,
    setEfficiencyN,
    cleaneffiN,
    efficiencyP,
    setEfficiencyP,
    cleaneffiP,
    efficiencyK,
    setEfficiencyK,
    cleaneffiK,
    resetData,
  } = useFertilizationInputs();

  const {
    targetNPercent,
    currentNPercent,
    getCurrentN,
    getTargetN,
    getTargetPPercent,
    currentPPercent,
    getCurrentP,
    getTargetP,
    getTargetKPercent,
    currentKPercent,
    getCurrentK,
    getTargetK,
  } = useFertilizationCalculations(
    inputN,
    inputP,
    inputK,
    pricesN,
    pricesP,
    pricesK,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
  );

  useFertilizationNavigation(
    isActive,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    efficiencyN,
    efficiencyP,
    efficiencyK,
  );

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:px-4 rounded-2xl">
        <NpkInputSection
          inputN={inputN}
          setInputN={setInputN}
          cleaninputn={cleaninputn}
          inputP={inputP}
          setInputP={setInputP}
          cleaninputp={cleaninputp}
          inputK={inputK}
          setInputK={setInputK}
          cleaninputk={cleaninputk}
          kgHa={kgHa}
        />

        <ExpectedGainsSection
          getCurrentN={getCurrentN}
          currentNPercent={currentNPercent}
          getCurrentP={getCurrentP}
          currentPPercent={currentPPercent}
          getCurrentK={getCurrentK}
          currentKPercent={currentKPercent}
          getTargetN={getTargetN}
          targetNPercent={targetNPercent}
          getTargetP={getTargetP}
          getTargetPPercent={getTargetPPercent}
          getTargetK={getTargetK}
          getTargetKPercent={getTargetKPercent}
          kgHa={kgHa}
        />

        <PricesSection
          pricesN={pricesN}
          setPricesN={setPricesN}
          pricesP={pricesP}
          setPricesP={setPricesP}
          pricesK={pricesK}
          setPricesK={setPricesK}
          efficiencyN={efficiencyN}
          setEfficiencyN={setEfficiencyN}
          efficiencyP={efficiencyP}
          setEfficiencyP={setEfficiencyP}
          efficiencyK={efficiencyK}
          setEfficiencyK={setEfficiencyK}
          kg={kg}
          onlyAc={onlyAc}
        />

        <TotalGainSection
          cleaneffiN={cleaneffiN}
          cleaneffiP={cleaneffiP}
          cleaneffiK={cleaneffiK}
          onlyAc={onlyAc}
        />

        {!isPDF && <ResetButton onReset={resetData} />}
      </div>
    </form>
  );
};

export default FertilizationBoard;
